/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import {
    IContentBlockAdditionalContentItemViewProps,
    IContentBlockAdditionalContentViewProps,
    IContentBlockViewProps,
    IContentBlockFullConfig
} from '@msdyn365-commerce-modules/content-block';
import * as React from 'react';
import { useIsMobile } from '../../../shared/hooks/useIsMobile';
import { IContentBlockProps } from '../definition-extensions/content-block.ext.props.autogenerated';
import { convertImageSettingsToWebp } from '../../../shared/functions/convert-image-to-webp';
import { ImageFetchPriority } from '@msdyn365-commerce/core';
import classNames from 'classnames';
import { HeadingStylingWrapper } from '../../../components';

const getAspectRatioCssVariables = (viewports: Msdyn365.IViewPort | undefined): Record<string, string> | undefined => {
    if (!viewports) {
        return undefined;
    }

    const ratios: Record<string, string> = {};

    for (const [key, { w, h }] of Object.entries(viewports)) {
        if (!w || !h) {
            continue;
        }

        ratios[`--ar-${key}`] = `${w}/${h}`;
    }

    return ratios;
};

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IContentBlockAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.additionalContentItems?.map((item: IContentBlockAdditionalContentItemViewProps) => {
                return (
                    <>
                        {item.heading}
                        <Node {...item.additionalContentItemContainer}>
                            {item.text}
                            <Node {...item.additionalContentItemLinks}>{item.links}</Node>
                        </Node>
                    </>
                );
            })}
        </Node>
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
const contentBlockView: React.FC<IContentBlockViewProps & IContentBlockProps<{}>> = props => {
    const {
        contentBlockContainer,
        imageContainer,
        detailsContainer,
        title,
        text,
        links,
        image,
        contentBlockAnchorTag,
        imageLink,
        imageAriaLabel,
        additionalContent,
        config,
        context
    } = props;

    const isMobile = useIsMobile(context);

    // @ts-expect-error HTML element need to be clear on run time.
    const hasImage: boolean = image.props.src;
    const imageClass: string = hasImage ? `${detailsContainer.className} withImage` : `${detailsContainer.className} withoutImage`;
    const detailsContainerClass: string = `${detailsContainer.className} --horizontal-${
        config?.horizontalAlignment || 'center'
    } --vertical-${config?.verticalAlignment || 'center'}`;

    const { desktopImageProps, mobileImageProps, finalContainerProps } = React.useMemo(() => {
        const mobileImageProps = {
            gridSettings: context.request.gridSettings ?? {},
            imageSettings: convertImageSettingsToWebp(config.mobileImage?.imageSettings)
        };

        const desktopImageProps = {
            gridSettings: context.request.gridSettings ?? {},
            imageSettings: convertImageSettingsToWebp(config.image?.imageSettings)
        };

        const aspectRatioCssVariables = getAspectRatioCssVariables(
            isMobile && config.mobileImage ? mobileImageProps.imageSettings?.viewports : desktopImageProps.imageSettings?.viewports
        );

        const finalContainerProps: IModuleProps = {
            ...contentBlockContainer,
            className: classNames(contentBlockContainer.className, {
                'stabilize-layout': config.stabilizeLayout ?? false
            }),
            ...(config.stabilizeLayout ? { style: aspectRatioCssVariables } : {})
        };

        return { mobileImageProps, desktopImageProps, finalContainerProps };
    }, [context.request.gridSettings, config.mobileImage, config.image, isMobile, config.stabilizeLayout, contentBlockContainer]);

    const mobileImage = (
        <Msdyn365.Image
            {...config.mobileImage}
            {...mobileImageProps}
            requestContext={context.request}
            preserveImageSpace={true}
            className='ms-content-block__mobile-image'
            editProps={{
                key: config.mobileImage ?? {},
                requestContext: context.request,
                moduleType: props.typeName,
                imagePropertyName: 'mobileImage',
                moduleId: props.id,
                layout: (config as IContentBlockFullConfig).msdyn365__moduleLayout
            }}
            shouldSkipToMainImage
            fetchPriority={config.fetchPriorityHigh ? ImageFetchPriority.High : undefined}
        />
    );

    const desktopImage = (
        <Msdyn365.Image
            {...config.image}
            {...desktopImageProps}
            requestContext={context.request}
            preserveImageSpace={true}
            editProps={{
                key: props.config.image ?? {},
                requestContext: context.request,
                moduleType: props.typeName,
                imagePropertyName: 'image',
                moduleId: props.id,
                layout: (props.config as IContentBlockFullConfig).msdyn365__moduleLayout
            }}
            shouldSkipToMainImage
            fetchPriority={config.fetchPriorityHigh ? ImageFetchPriority.High : undefined}
        />
    );

    if (imageLink) {
        return (
            <Module {...finalContainerProps}>
                <Node
                    {...contentBlockAnchorTag}
                    href={imageLink}
                    className={contentBlockAnchorTag ? contentBlockAnchorTag.className : ''}
                    aria-label={imageAriaLabel}
                >
                    {isMobile && config.mobileImage ? (
                        <Node {...imageContainer}>{mobileImage}</Node>
                    ) : (
                        <Node {...imageContainer}>{desktopImage}</Node>
                    )}
                </Node>
                {!!config.titleProps && (
                    <Node className={detailsContainerClass}>
                        <HeadingStylingWrapper headingConfig={config.titleProps} varPrefix='content-block-title'>
                            {title}
                        </HeadingStylingWrapper>
                        {text}
                        {links}
                        {additionalContent && renderAdditionalContent(additionalContent)}
                    </Node>
                )}
            </Module>
        );
    }
    return (
        <Module {...finalContainerProps}>
            {isMobile && config.mobileImage ? (
                <Node {...imageContainer}>{mobileImage}</Node>
            ) : (
                <Node {...imageContainer}>{desktopImage}</Node>
            )}
            {!!config.titleProps && (
                <Node className={imageClass}>
                    <HeadingStylingWrapper headingConfig={config.titleProps} varPrefix='content-block-title'>
                        {title}
                    </HeadingStylingWrapper>
                    {text}
                    {links}
                    {additionalContent && renderAdditionalContent(additionalContent)}
                </Node>
            )}
        </Module>
    );
};

export default contentBlockView;
